import { type Dispatch, type Reducer } from 'react';
import { OFPageType } from '../../generated/api/oFPageType.js';
import { OpenFormAnswers, type OpenFormContext } from './OpenFormAnswers.js';
import { type OpenFormIndicator } from './OpenFormComponents/OpenFormLoadingIndicator.js';
import { type OpenFormNotification } from './OpenFormComponents/OpenFormNotifications.js';
import { type OpenForm } from './OpenFormHooks/useOpenForm.js';
import { type OpenFormStore } from './OpenFormStore.js';

export type OpenFormAction<K extends keyof typeof actions = keyof typeof actions> = ReturnType<(typeof actions)[K]>;

export type OpenFormActions = {
  [K in keyof typeof actions]: (
    payload: (typeof actions)[K] extends (payload: infer T) => void ? T : never
  ) => OpenFormActions;
};

export type OpenFormState = { readonly [P in keyof OpenFormReducer]-?: OpenFormReducer[P] };

const type: unique symbol = Symbol('OpenFormAction');

const actions = {
  answers: (payload: OpenFormAnswers | undefined) => ({
    [type]: 'answers' as const,
    payload,
  }),

  choices: (payload: { question: string; value: string | string[] | undefined }) => ({
    [type]: 'choices' as const,
    payload,
  }),

  confirm: (payload: boolean) => ({
    [type]: 'confirm' as const,
    payload,
  }),

  context: <K extends keyof OpenFormContext>(payload: { question: string; key: K; patch?: OpenFormContext[K] }) => ({
    [type]: 'context' as const,
    payload,
  }),

  form: (payload: OpenForm | undefined) => ({
    [type]: 'form' as const,
    payload,
  }),

  indicator: (payload: OpenFormIndicator & { key: string }) => ({
    [type]: 'indicator' as const,
    payload,
  }),

  notification: (payload: OpenFormNotification | undefined) => ({
    [type]: 'notification' as const,
    payload,
  }),

  page: (payload: number | undefined) => ({
    [type]: 'page' as const,
    payload,
  }),
} as const;

export class OpenFormReducer {
  private constructor(
    readonly answers: OpenFormAnswers = new OpenFormAnswers(),
    readonly confirm: boolean = false,
    readonly disabled: boolean = false,
    readonly form: OpenForm | undefined = undefined,
    readonly indicators: Map<string, OpenFormIndicator> = new Map(),
    readonly notifications: Set<OpenFormNotification> = new Set(),
    readonly page = 1
  ) {}

  static actions(dispatch: Dispatch<OpenFormAction>, target = {}, fns = Object.entries(actions)): OpenFormActions {
    return Object.assign(
      target,
      ...fns.map(([name, action]) => ({ [name]: (payload: never) => (dispatch(action(payload)) as unknown) || target }))
    );
  }

  static reducer(store: OpenFormStore): Reducer<OpenFormState, OpenFormAction> {
    return (state: OpenFormState, action: OpenFormAction): OpenFormState => {
      switch (action[type]) {
        case 'choices':
          return (state = this.#choices(this.#reduce(state, action), action)).form?.snapshot(state, store) || state;
        case 'answers':
        case 'context':
        case 'form':
        case 'page':
          return (state = this.#reduce(state, action)).form?.snapshot(state, store) || state;
        case 'confirm':
        case 'indicator':
        case 'notification':
          return this.#disabled(this.#reduce(state, action));
      }
    };
  }

  static state(): OpenFormState {
    return { ...new OpenFormReducer() } as const;
  }

  static #choices(state: OpenFormState, { payload: { question, value } }: OpenFormAction<'choices'>): OpenFormState {
    if (state.form) {
      const { answers, form, page } = state;
      const section = form.sections[page - 1];
      switch (section?.pageType) {
        case OFPageType.QUESTION_SUBSCRIPTION_TYPE: {
          const patch = section.questions.flatMap(q => (q.guid === question ? form.linkedValues(q, value) : []));
          patch.length && answers.assign(question, 'subscriptionTypes', patch);
        }
      }
    }
    return state;
  }

  static #disabled(state: OpenFormState): OpenFormState {
    const { confirm, indicators, notifications } = state;
    const disabled = confirm || !!notifications.size || Array.from(indicators.values()).some(({ disable }) => disable);
    return state.disabled !== disabled ? { ...state, disabled } : state;
  }

  static #reduce(state: OpenFormState, action: OpenFormAction): OpenFormState {
    switch (action[type]) {
      case 'answers': {
        const answers = action.payload;
        return { ...state, answers: new OpenFormAnswers(answers) };
      }
      case 'choices': {
        const { question, value } = action.payload;
        return value?.length
          ? typeof value === 'string'
            ? { ...state, answers: new OpenFormAnswers(state.answers.assign(question, 'choices', [value])) }
            : { ...state, answers: new OpenFormAnswers(state.answers.assign(question, 'choices', value)) }
          : state.answers.delete(question)
            ? { ...state, answers: new OpenFormAnswers(state.answers) }
            : state;
      }
      case 'confirm': {
        const confirm = action.payload;
        return state.confirm !== confirm ? { ...state, confirm } : state;
      }
      case 'context': {
        const { question, key, patch } = action.payload;
        return { ...state, answers: new OpenFormAnswers(state.answers.assign(question, key, patch)) };
      }
      case 'form': {
        const form = action.payload;
        return { ...state, form };
      }
      case 'indicator': {
        const { disable, message, key } = action.payload;
        return message !== undefined
          ? { ...state, indicators: new Map(state.indicators.set(key, { disable, message })) }
          : state.indicators.delete(key)
            ? { ...state, indicators: new Map(state.indicators) }
            : state;
      }
      case 'notification': {
        const notification = action.payload;
        return notification
          ? { ...state, notifications: new Set(state.notifications.add(notification)) }
          : state.notifications.delete(state.notifications.values().next().value)
            ? { ...state, notifications: new Set(state.notifications) }
            : state;
      }
      case 'page': {
        const page = (action.payload && Math.max(Math.floor(action.payload), 1)) || 1;
        return state.page !== page ? { ...state, page } : state;
      }
    }
  }
}
